import { isDemoSession, getDemoPreference, setDemoPreference, getDemoSession } from '../accountSetup/demoSession';
import { getAccessToken, getSessionInfo } from '../authentication/supabaseService';

export interface BackendUserDefault {
  user_id: string;
  key: string;
  value: string;
  updated_at: string;
}

export interface FrontendUserDefault {
  userId: string;
  key: string;
  value: string;
  updatedAt: string;
}

function mapBackendToFrontend(d: BackendUserDefault): FrontendUserDefault {
  return {
    userId: d.user_id,
    key: d.key,
    value: d.value,
    updatedAt: d.updated_at,
  };
}

/**
 * Create or update a user‐default.
 */
export async function setUserDefault(
  key: string,
  value: string,
  expectedUserId?: string
): Promise<FrontendUserDefault> {
  if (isDemoSession() && !key.startsWith('onboarding_tour_')) {
    setDemoPreference(key, value);
    return { userId: getDemoSession()!.actorId, key, value, updatedAt: new Date().toISOString() };
  }
  let token: string;
  if (expectedUserId && process.env.REACT_APP_MOCK_AUTH !== 'true') {
    // Bind delayed preference saves to the login that initiated them. The token
    // and identity come from the same snapshot, even if another login follows.
    const { session } = await getSessionInfo();
    if (!session?.access_token || session.user.id !== expectedUserId) {
      throw new Error('User changed before saving preference');
    }
    token = session.access_token;
  } else {
    token = await getAccessToken();
  }
  const res = await fetch('/api/userdefaults/set', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ key, value }),
  });
  if (!res.ok) {
    throw new Error(`Failed to set default: ${res.status} ${res.statusText}`);
  }
  const backend: BackendUserDefault = await res.json();
  return mapBackendToFrontend(backend);
}

/**
 * Fetch a single user‐default by key.
 */
export async function getUserDefault(
  key: string
): Promise<FrontendUserDefault | null> {
  if (isDemoSession() && !key.startsWith('onboarding_tour_')) {
    const value = getDemoPreference(key);
    return value === null ? null : { userId: getDemoSession()!.actorId, key, value, updatedAt: new Date().toISOString() };
  }
  const token = await getAccessToken();
  const res = await fetch(`/api/userdefaults/${encodeURIComponent(key)}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Failed to fetch default: ${res.status} ${res.statusText}`);
  }
  const backend: BackendUserDefault = await res.json();
  return mapBackendToFrontend(backend);
}

/**
 * Delete a user‐default by key.
 */
export async function deleteUserDefault(
  key: string
): Promise<void> {
  if (isDemoSession()) { setDemoPreference(key, null); return; }
  const token = await getAccessToken();
  const res = await fetch(`/api/userdefaults/${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete default: ${res.status} ${res.statusText}`);
  }
}

/**
 * Batch fetch multiple user defaults by keys.
 * Returns a map of key -> value for all found defaults.
 */
export async function getUserDefaults(
  keys: string[]
): Promise<Record<string, string>> {
  if (isDemoSession()) {
    const local: Record<string, string> = {};
    const persisted = keys.filter(key => key.startsWith('onboarding_tour_'));
    for (const key of keys) {
      const value = getDemoPreference(key);
      if (!persisted.includes(key) && value !== null) local[key] = value;
    }
    if (!persisted.length) return local;
    const token = await getAccessToken();
    const response = await fetch('/api/userdefaults/batch', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ keys: persisted }) });
    if (!response.ok) throw new Error('Failed to fetch tour preferences');
    const rows: BackendUserDefault[] = await response.json();
    rows.forEach(row => { local[row.key] = row.value; });
    return local;
  }
  const token = await getAccessToken();
  const res = await fetch('/api/userdefaults/batch', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ keys }),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch defaults: ${res.status} ${res.statusText}`);
  }
  const backendList: BackendUserDefault[] = await res.json();

  // Convert array to map for easy lookup
  const result: Record<string, string> = {};
  backendList.forEach(item => {
    result[item.key] = item.value;
  });

  return result;
}
